var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

import React from 'react';
import PropTypes from 'prop-types';
import ScalerContext from '../../context/Scaler';
import GriffPropTypes, { seriesPropType } from '../../utils/proptypes';
import { createYScale, createXScale } from '../../utils/scale-helpers';
import Axes from '../../utils/Axes';
var propTypes = {
  grid: GriffPropTypes.grid,
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  series: seriesPropType.isRequired,
  axes: PropTypes.shape({
    x: PropTypes.oneOf(['x', 'time'])
  }),
  // These are all populated by Griff.
  subDomainsByItemId: GriffPropTypes.subDomainsByItemId.isRequired
};
var defaultProps = {
  axes: {
    x: 'x'
  },
  grid: null
};
var DEFAULT_COLOR = '#666';
var DEFAULT_OPACITY = 0.6;
var DEFAULT_STROKE_WIDTH = 1;

var GridLines = function GridLines(_ref) {
  var grid = _ref.grid,
      height = _ref.height,
      width = _ref.width,
      series = _ref.series,
      subDomainsByItemId = _ref.subDomainsByItemId,
      axes = _ref.axes;

  if (!grid) {
    return null;
  }

  var x = grid.x,
      y = grid.y;

  if (!x && !y) {
    return null;
  }

  var lines = [];

  if (y) {
    if (y.seriesIds) {
      var seriesIdMap = y.seriesIds.reduce(function (dict, id) {
        var _extends2;

        return _extends({}, dict, (_extends2 = {}, _extends2[id] = true, _extends2));
      }, {});
      series.filter(function (s) {
        return seriesIdMap[s.id];
      }).forEach(function (s) {
        // This is heavily inspired by YAxis -- maybe we could consolidate?
        var scale = createYScale(Axes.y(subDomainsByItemId[s.id]), height);
        var nTicks = y.count || Math.floor(height / 50) || 1;
        var values = scale.ticks(nTicks);
        values.forEach(function (v) {
          lines.push(React.createElement('line', {
            key: 'horizontal-' + s.id + '-' + v,
            className: 'grid-line grid-line-horizontal',
            opacity: y.opacity || grid.opacity || DEFAULT_OPACITY,
            stroke: y.color === null ? s.color : y.color || grid.color || DEFAULT_COLOR,
            strokeWidth: y.strokeWidth || grid.strokeWidth || DEFAULT_STROKE_WIDTH,
            x1: 0,
            x2: width,
            y1: (y.strokeWidth || grid.strokeWidth || DEFAULT_STROKE_WIDTH) / 2,
            y2: (y.strokeWidth || grid.strokeWidth || DEFAULT_STROKE_WIDTH) / 2,
            transform: 'translate(0, ' + scale(v) + ')'
          }));
        });
      });
    } else if (y.pixels) {
      for (var position = height % y.pixels / 2; position <= height; position += y.pixels) {
        lines.push(React.createElement('line', {
          key: 'horizontal-' + position,
          className: 'grid-line grid-line-horizontal',
          x1: 0,
          x2: width,
          y1: position,
          y2: position,
          stroke: y.color || grid.color || DEFAULT_COLOR,
          strokeWidth: y.strokeWidth || grid.strokeWidth || DEFAULT_STROKE_WIDTH,
          opacity: y.opacity || grid.opacity || DEFAULT_OPACITY
        }));
      }
    } else if (y.count) {
      var interval = height / y.count;

      for (var _position = interval / 2; _position <= height; _position += interval) {
        lines.push(React.createElement('line', {
          key: 'horizontal-' + _position,
          className: 'grid-line grid-line-horizontal',
          x1: 0,
          x2: width,
          y1: _position,
          y2: _position,
          stroke: y.color || grid.color || DEFAULT_COLOR,
          strokeWidth: y.strokeWidth || grid.strokeWidth || DEFAULT_STROKE_WIDTH,
          opacity: y.opacity || grid.opacity || DEFAULT_OPACITY
        }));
      }
    }
  }

  if (x) {
    if (x.pixels) {
      for (var _position2 = width % x.pixels / 2; _position2 <= width; _position2 += x.pixels) {
        lines.push(React.createElement('line', {
          key: 'vertical-' + _position2,
          className: 'grid-line grid-line-vertical',
          y1: 0,
          y2: height,
          x1: _position2,
          x2: _position2,
          stroke: x.color || grid.color || DEFAULT_COLOR,
          strokeWidth: x.strokeWidth || grid.strokeWidth || DEFAULT_STROKE_WIDTH,
          opacity: x.opacity || grid.opacity || DEFAULT_OPACITY
        }));
      }
    } else if (x.ticks !== undefined) {
      // This heavily inspired by XAxis -- maybe we can consolidate them?
      // FIXME: Remove this when we support multiple X axes
      var timeSubDomain = subDomainsByItemId[Object.keys(subDomainsByItemId)[0]][axes.x];
      var scale = createXScale(timeSubDomain, width);
      var values = scale.ticks(x.ticks || Math.floor(width / 100) || 1);
      values.forEach(function (v) {
        lines.push(React.createElement('line', {
          key: 'vertical-' + +v,
          className: 'grid-line grid-line-vertical',
          opacity: x.opacity || grid.opacity || DEFAULT_OPACITY,
          stroke: x.color || grid.color || DEFAULT_COLOR,
          strokeWidth: x.strokeWidth || grid.strokeWidth || DEFAULT_STROKE_WIDTH,
          y1: 0,
          y2: height,
          x1: (x.strokeWidth || grid.strokeWidth || DEFAULT_STROKE_WIDTH) / 2,
          x2: (x.strokeWidth || grid.strokeWidth || DEFAULT_STROKE_WIDTH) / 2,
          transform: 'translate(' + scale(v) + ', 0)'
        }));
      });
    } else if (x.count) {
      var _interval = width / x.count;

      for (var _position3 = _interval / 2; _position3 <= width; _position3 += _interval) {
        lines.push(React.createElement('line', {
          key: 'vertical-' + _position3,
          className: 'grid-line grid-line-vertical',
          y1: 0,
          y2: height,
          x1: _position3,
          x2: _position3,
          stroke: x.color || grid.color || DEFAULT_COLOR,
          strokeWidth: x.strokeWidth || grid.strokeWidth || DEFAULT_STROKE_WIDTH,
          opacity: x.opacity || grid.opacity || DEFAULT_OPACITY
        }));
      }
    }
  }

  return lines;
};

GridLines.propTypes = process.env.NODE_ENV !== "production" ? propTypes : {};
GridLines.defaultProps = defaultProps;
export default (function (props) {
  return React.createElement(ScalerContext.Consumer, null, function (_ref2) {
    var series = _ref2.series,
        subDomainsByItemId = _ref2.subDomainsByItemId;
    return React.createElement(GridLines, _extends({}, props, {
      series: series,
      subDomainsByItemId: subDomainsByItemId
    }));
  });
});